import React, { useEffect, useState } from "react";
import { Table, Button, InputNumber, Popconfirm, message } from "antd";

import {
  PlusOutlined,
  SaveOutlined,
  EditOutlined,
  DeleteOutlined,
  CloseOutlined,
} from "@ant-design/icons";

import {
  removePricesProducts,
  addPricesProducts,
  editPricesProducts,
  fetchProductPricesById,
} from "../../utils/fetchProducts";

const sortByMinQty = (prices) =>
  [...prices].sort((a, b) => Number(a.min_qty) - Number(b.min_qty));

const { format: formatCLP } = new Intl.NumberFormat("es-CL", {
  style: "currency",
  currency: "CLP",
});

const priceInputFormatter = (value) =>
  value === undefined || value === null || value === "" ? "" : formatCLP(value);

const priceInputParser = (value) =>
  value ? value.toString().replace(/[^\d]/g, "") : "";

const PricesProducts = ({ productID }) => {
  const [prices, setPrices] = useState([]);
  const [isPricesLoading, setIsPricesLoading] = useState(true);

  const [newPrice, setNewPrice] = useState({ min_qty: "", price: "" });
  const [loadingAddButton, setLoadingAddButton] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({ min_qty: "", price: "" });
  const [loadingRowId, setLoadingRowId] = useState(null);

  const reloadPrices = async () => {
    const freshPrices = await fetchProductPricesById(Number(productID));
    setPrices(
      sortByMinQty(freshPrices).map((item) => ({ ...item, key: item.id }))
    );
  };

  useEffect(() => {
    const loadPrices = async () => {
      setIsPricesLoading(true);
      try {
        await reloadPrices();
      } catch (error) {
        message.error("Error al obtener los precios");
        console.error("error", error);
      } finally {
        setIsPricesLoading(false);
      }
    };
    loadPrices();
  }, []);

  const handleDelete = async (id) => {
    try {
      await removePricesProducts([id]);
      setPrices(prices.filter((item) => item.id !== id));
      message.success("Precio eliminado correctamente!");
    } catch (error) {
      message.error("Error al eliminar el precio");
    }
  };

  const handleAdd = async () => {
    if (newPrice.min_qty && newPrice.price !== "") {
      setLoadingAddButton(true);
      try {
        await addPricesProducts(productID, newPrice.min_qty, newPrice.price);
        await reloadPrices();
        setNewPrice({ min_qty: "", price: "" });
        message.success("Precio añadido correctamente!");
      } catch (error) {
        message.error("Error al agregar el precio.");
      } finally {
        setLoadingAddButton(false);
      }
    } else {
      message.warning("Debes indicar una cantidad mínima y un precio.");
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleAdd();
    }
  };

  const handleEdit = (record) => {
    setEditingId(record.id);
    setEditValues({ min_qty: record.min_qty, price: record.price });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
  };

  const handleSaveEdit = async (record) => {
    setLoadingRowId(record.id);
    try {
      if (Number(editValues.min_qty) === Number(record.min_qty)) {
        await editPricesProducts(productID, editValues.min_qty, editValues.price);
      } else {
        // Changing the tier's min_qty means replacing the row: min_qty is the
        // key the edit endpoint matches on, so it can't rename it in place.
        await removePricesProducts([record.id]);
        await addPricesProducts(productID, editValues.min_qty, editValues.price);
      }
      await reloadPrices();
      setEditingId(null);
      message.success("Precio actualizado correctamente!");
    } catch (error) {
      message.error("Error al actualizar el precio.");
    } finally {
      setLoadingRowId(null);
    }
  };

  const columns = [
    {
      title: "Desde (Cant. Mínima)",
      dataIndex: "min_qty",
      width: "35%",
      render: (value, record) =>
        editingId === record.id ? (
          <InputNumber
            min={1}
            style={{ width: 140 }}
            value={editValues.min_qty}
            onChange={(val) => setEditValues({ ...editValues, min_qty: val })}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                handleSaveEdit(record);
              }
            }}
          />
        ) : (
          value
        ),
    },
    {
      title: "Precio Unitario",
      dataIndex: "price",
      width: "35%",
      render: (value, record) =>
        editingId === record.id ? (
          <InputNumber
            min={0}
            style={{ width: 160 }}
            value={editValues.price}
            onChange={(val) => setEditValues({ ...editValues, price: val })}
            formatter={priceInputFormatter}
            parser={priceInputParser}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                handleSaveEdit(record);
              }
            }}
          />
        ) : (
          formatCLP(value)
        ),
    },
    {
      title: "Acción",
      width: "10%",
      dataIndex: "action",
      render: (_, record) =>
        editingId === record.id ? (
          <span>
            <Button
              type="link"
              onClick={() => handleSaveEdit(record)}
              icon={<SaveOutlined />}
              loading={loadingRowId === record.id}
            />
            <Button
              type="link"
              onClick={handleCancelEdit}
              icon={<CloseOutlined />}
              disabled={loadingRowId === record.id}
            />
          </span>
        ) : (
          <span>
            <Button
              type="link"
              onClick={() => handleEdit(record)}
              icon={<EditOutlined />}
              disabled={editingId !== null}
            />
            <Popconfirm
              title={
                <div>
                  <div>Esta acción no se puede deshacer.</div>
                  <div>¿Confirma eliminación?</div>
                </div>
              }
              onConfirm={() => handleDelete(record.id)}
            >
              <Button
                type="link"
                icon={<DeleteOutlined />}
                disabled={editingId !== null}
              />
            </Popconfirm>
          </span>
        ),
    },
  ];

  return (
    <>
      <div className="w-full outline outline-gray-200 rounded-lg p-4 mb-4">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-gray-700 text-sm mb-1">
              Cantidad Mínima
            </label>
            <InputNumber
              min={1}
              style={{ width: 160 }}
              placeholder="Ej: 1"
              value={newPrice.min_qty}
              onChange={(value) => setNewPrice({ ...newPrice, min_qty: value })}
              onKeyPress={handleKeyPress}
            />
          </div>
          <div>
            <label className="block text-gray-700 text-sm mb-1">
              Precio Unitario
            </label>
            <InputNumber
              min={0}
              style={{ width: 180 }}
              placeholder="Ej: $1.000"
              value={newPrice.price}
              onChange={(value) => setNewPrice({ ...newPrice, price: value })}
              onKeyPress={handleKeyPress}
              formatter={priceInputFormatter}
              parser={priceInputParser}
            />
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
            loading={loadingAddButton}
          >
            Agregar Precio
          </Button>
        </div>
      </div>
      <div className="w-full flex justify-center">
        <div className="w-full">
          <Table
            dataSource={prices}
            columns={columns}
            loading={isPricesLoading}
            pagination={false}
          />
        </div>
      </div>
    </>
  );
};

export default PricesProducts;
